import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Loader2, Trophy, BedDouble } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceArea, Legend } from "recharts";

const iso = (d) => d.toISOString().slice(0, 10);
const fmtD = (s) => new Date(s + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
const fmtDW = (s) => new Date(s + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
const n1 = (v) => (v == null ? "—" : Number(v).toFixed(1));

function rangeFor(kind) {
  const t = new Date();
  if (kind === "week") { const s = new Date(t); s.setDate(t.getDate() - ((t.getDay() + 6) % 7)); return [iso(s), iso(t)]; }
  if (kind === "month") return [iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(t)];
  return [null, null];
}

function weekly(rows) {
  const m = {};
  rows.forEach((r) => {
    const d = new Date(r.date + "T00:00:00");
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const k = iso(d);
    const w = (m[k] = m[k] || { date: k, rooms: 0, hours: 0 });
    w.rooms += r.rooms; w.hours += r.hours;
  });
  return Object.values(m).sort((a, b) => a.date.localeCompare(b.date)).map((w) => ({ ...w, mpr: w.rooms ? Math.round((w.hours * 600) / w.rooms) / 10 : null }));
}

function TrendChart({ rows, range, byWeek }) {
  const data = (byWeek ? weekly(rows) : rows).map((r) => ({ ...r, label: byWeek ? `Wk ${fmtD(r.date)}` : fmtD(r.date) }));
  if (data.length === 0) return <p className="py-8 text-center text-sm text-muted-foreground">No housekeeping rooms saved in this range.</p>;
  return (
    <div className="h-64 w-full" data-testid="hk-sc-chart">
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="r" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="m" orientation="right" tick={{ fontSize: 11 }} domain={[0, (max) => Math.max(60, Math.ceil(max / 10) * 10)]} />
          <ReferenceArea yAxisId="m" y1={range[0]} y2={range[1]} fill="#10b981" fillOpacity={0.08} />
          <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v, k) => (k === "Min / room" ? n1(v) : v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="r" dataKey="rooms" name="Rooms" fill="hsl(var(--primary))" fillOpacity={0.35} radius={[2, 2, 0, 0]} />
          <Line yAxisId="m" type="monotone" dataKey="mpr" name="Min / room" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function DetailDialog({ hk, range, onClose }) {
  const [byWeek, setByWeek] = useState(false);
  return (
    <Dialog open={!!hk} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="hk-sc-detail">
        {hk && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading">{hk.name}</DialogTitle>
              <DialogDescription>{hk.days} days · {hk.rooms} rooms · {hk.hours.toFixed(2)} hrs · <span className={hk.out_of_range ? "font-semibold text-red-600" : ""}>{n1(hk.mpr)} min/room</span> · {n1(hk.rooms_per_day)} rooms/day</DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-1">
              <Button size="sm" variant={byWeek ? "outline" : "default"} onClick={() => setByWeek(false)}>By day</Button>
              <Button size="sm" variant={byWeek ? "default" : "outline"} onClick={() => setByWeek(true)}>By week</Button>
            </div>
            <TrendChart rows={hk.daily} range={range} byWeek={byWeek} />
            <table className="w-full text-sm" data-testid="hk-sc-detail-table">
              <thead><tr className="bg-muted/50 text-left text-[0.65rem] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2">Date</th><th className="px-3 py-2 text-right">Hours</th><th className="px-3 py-2 text-right">Stayover</th><th className="px-3 py-2 text-right">Checkout</th><th className="px-3 py-2 text-right">Rooms</th><th className="px-3 py-2 text-right">Min / Room</th>
              </tr></thead>
              <tbody>{hk.daily.map((x) => (
                <tr key={x.date} className={`border-t border-border ${x.out_of_range ? "bg-red-50 dark:bg-red-950/20" : ""}`} data-testid={`hk-sc-day-${x.date}`}>
                  <td className="px-3 py-1.5">{fmtDW(x.date)}</td><td className="px-3 py-1.5 text-right tabular-nums">{x.hours.toFixed(2)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{x.stayover}</td><td className="px-3 py-1.5 text-right tabular-nums">{x.checkout}</td>
                  <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{x.rooms}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${x.out_of_range ? "font-semibold text-red-600 dark:text-red-400" : ""}`}>{x.hours > 0 && !x.rooms ? "0 rooms" : n1(x.mpr)}</td>
                </tr>
              ))}</tbody>
            </table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, testid, warn }) {
  return (
    <div className="border border-border bg-card px-4 py-3" data-testid={testid}>
      <div className="label-caps text-[0.6rem] text-muted-foreground">{label}</div>
      <div className={`mt-1 font-heading text-2xl font-bold tabular-nums ${warn ? "text-red-600 dark:text-red-400" : ""}`}>{value}</div>
    </div>
  );
}

export const HkScorecard = ({ propertyId, propertyName }) => {
  const [kind, setKind] = useState("period");
  const [custom, setCustom] = useState([iso(new Date(Date.now() - 13 * 864e5)), iso(new Date())]);
  const [data, setData] = useState(null);
  const [byWeek, setByWeek] = useState(false);
  const [sel, setSel] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [start, end] = useMemo(() => (kind === "custom" ? custom : rangeFor(kind)), [kind, custom]);
  const params = useMemo(() => ({ property_id: propertyId, ...(start ? { start, end } : {}) }), [propertyId, start, end]);

  const load = useCallback(() => {
    if (!propertyId) return;
    setData(null);
    api.get("/housekeeping/scorecard", { params }).then((r) => setData(r.data))
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load scorecard"); setData({ housekeepers: [], trend: [], totals: {}, range: [15, 45] }); });
  }, [params, propertyId]);
  useEffect(() => { load(); }, [load]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get("/housekeeping/scorecard/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(propertyName || "Property").replace(/\s+/g, "_")}_HK_Scorecard_${data?.start}_to_${data?.end}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  const range = data?.range || [15, 45];
  const t = data?.totals || {};
  const flagged = (data?.housekeepers || []).filter((h) => h.out_of_range).length;
  const kinds = [["period", "Pay period"], ["week", "This week"], ["month", "This month"], ["custom", "Custom"]];

  return (
    <div className="space-y-5" data-testid="hk-scorecard">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap gap-1">
          {kinds.map(([k, l]) => <Button key={k} size="sm" variant={kind === k ? "default" : "outline"} onClick={() => setKind(k)} data-testid={`hk-sc-range-${k}`}>{l}</Button>)}
        </div>
        {kind === "custom" && (
          <div className="flex items-center gap-2">
            <Input type="date" value={custom[0]} onChange={(e) => setCustom([e.target.value, custom[1]])} className="w-40" data-testid="hk-sc-start" />
            <span className="text-muted-foreground">–</span>
            <Input type="date" value={custom[1]} onChange={(e) => setCustom([custom[0], e.target.value])} className="w-40" data-testid="hk-sc-end" />
          </div>
        )}
        {data?.start && <span className="text-xs text-muted-foreground" data-testid="hk-sc-dates">{fmtD(data.start)} – {fmtD(data.end)} · standard {range[0]}–{range[1]} min/room</span>}
        <Button className="ml-auto" variant="outline" size="sm" onClick={exportXlsx} disabled={!data || exporting} data-testid="hk-sc-export">
          {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Export Excel
        </Button>
      </div>

      {data === null ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Rooms cleaned" value={t.rooms ?? 0} testid="hk-sc-total-rooms" />
            <Stat label="Housekeeping hours" value={(t.hours ?? 0).toFixed(2)} testid="hk-sc-total-hours" />
            <Stat label="Avg min / room" value={n1(t.mpr)} testid="hk-sc-avg-mpr" warn={t.mpr != null && (t.mpr < range[0] || t.mpr > range[1])} />
            <Stat label="Outside standard" value={`${flagged} / ${t.housekeepers ?? 0}`} testid="hk-sc-flagged" warn={flagged > 0} />
          </div>

          <div className="border border-border bg-card p-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-bold"><BedDouble className="h-4 w-4" /> Property trend <span className="text-xs font-normal text-muted-foreground">(green band = standard)</span></h3>
              <div className="flex gap-1">
                <Button size="sm" variant={byWeek ? "outline" : "default"} onClick={() => setByWeek(false)} data-testid="hk-sc-byday">By day</Button>
                <Button size="sm" variant={byWeek ? "default" : "outline"} onClick={() => setByWeek(true)} data-testid="hk-sc-byweek">By week</Button>
              </div>
            </div>
            <TrendChart rows={data.trend} range={range} byWeek={byWeek} />
          </div>

          {data.housekeepers.length === 0 ? (
            <div className="border border-dashed border-border py-10 text-center text-sm text-muted-foreground" data-testid="hk-sc-empty">No housekeeper rooms or hours saved on the Daily Report for this range.</div>
          ) : (
            <div className="overflow-x-auto border border-border">
              <table className="w-full text-sm" data-testid="hk-sc-table">
                <thead><tr className="bg-muted/50 text-left text-[0.65rem] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2">Housekeeper</th><th className="px-3 py-2 text-right">Days</th><th className="px-3 py-2 text-right">Hours</th>
                  <th className="px-3 py-2 text-right">Stayover</th><th className="px-3 py-2 text-right">Checkout</th><th className="px-3 py-2 text-right">Rooms</th>
                  <th className="px-3 py-2 text-right">Rooms / Day</th><th className="px-3 py-2 text-right">Min / Room</th><th className="px-3 py-2 text-right">Days off standard</th>
                </tr></thead>
                <tbody>{data.housekeepers.map((h, i) => (
                  <tr key={h.employee_id} onClick={() => setSel(h)} className={`cursor-pointer border-t border-border transition-colors hover:bg-muted/40 ${h.out_of_range ? "bg-red-50/70 dark:bg-red-950/20" : ""}`} data-testid={`hk-sc-row-${h.employee_id}`}>
                    <td className="px-3 py-2 font-medium text-primary">{i === 0 && !h.out_of_range && h.mpr != null && <Trophy className="mr-1 inline h-3.5 w-3.5 text-amber-500" />}{h.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{h.days}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{h.hours.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{h.stayover}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{h.checkout}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">{h.rooms}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{n1(h.rooms_per_day)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${h.out_of_range ? "font-bold text-red-600 dark:text-red-400" : ""}`} data-testid={`hk-sc-mpr-${h.employee_id}`}>{h.mpr == null ? "0 rooms" : n1(h.mpr)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${h.bad_days ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}`}>{h.bad_days}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-muted-foreground">Built from rooms and hours saved on the Daily Report (Housekeeping position only). Click a housekeeper for their day-by-day detail.</p>
        </>
      )}
      <DetailDialog hk={sel} range={range} onClose={() => setSel(null)} />
    </div>
  );
};
